import * as React from 'react';
import { cn } from '../lib/cn';
import { tablePatterns } from '../design-system/patterns/tables';
import { UniversalEmptyState } from './UniversalEmptyState';
import { UniversalLoadingState } from './UniversalLoadingState';

export interface UniversalTableColumn<T> {
  /** Stable key — used for sort state and React keys. */
  key: string;
  header: React.ReactNode;
  /** Cell renderer. Receives the row and the row index. */
  render: (row: T, index: number) => React.ReactNode;
  /** Optional sort accessor. When provided, the header becomes sortable. */
  sortValue?: (row: T) => string | number | Date | null | undefined;
  /** Right-align numeric columns (currencies, counts). */
  align?: 'left' | 'right' | 'center';
  /** Tailwind class to control width (e.g. 'w-32', 'w-1/4'). */
  width?: string;
  /** Hide on small screens. */
  hideOnMobile?: boolean;
}

export interface UniversalTableProps<T> {
  data: T[];
  columns: UniversalTableColumn<T>[];
  /** Row identity — defaults to row index. Pass a stable id for keyed rows. */
  getRowKey?: (row: T, index: number) => string | number;
  /** Click handler — when set, rows render as clickable. */
  onRowClick?: (row: T, index: number) => void;
  isLoading?: boolean;
  error?: Error | null;
  /** Empty-state config — rendered when `data.length === 0` and not loading. */
  empty?: {
    title: string;
    description?: string;
    icon?: React.ComponentType<{ className?: string }>;
    action?: React.ReactNode;
  };
  /** Density — affects cell padding. */
  density?: 'compact' | 'default' | 'relaxed';
  /** Caption rendered above the table (e.g. "5 invoices"). */
  caption?: React.ReactNode;
  className?: string;
}

type SortState = { key: string; direction: 'asc' | 'desc' } | null;

/**
 * UniversalTable — opinionated table for static or small-to-medium row sets.
 *
 * Capabilities:
 *  - Column-based config (no JSX-as-API)
 *  - Optional client-side sorting (supply `sortValue` on a column)
 *  - Built-in loading / empty / error states
 *  - Density, alignment, mobile column hiding
 *
 * For large grids with filters, virtualisation, or column hiding UI, use
 * UniversalDataTable (TanStack-based) instead.
 */
export function UniversalTable<T>({
  data,
  columns,
  getRowKey,
  onRowClick,
  isLoading,
  error,
  empty,
  density = 'default',
  caption,
  className,
}: UniversalTableProps<T>) {
  const [sort, setSort] = React.useState<SortState>(null);

  const sortedData = React.useMemo(() => {
    if (!sort) return data;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return data;
    const accessor = col.sortValue;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [data, columns, sort]);

  const cellPadding = tablePatterns.density[density];

  return (
    <div className={cn(tablePatterns.wrapper, className)}>
      {caption && (
        <div className="flex items-center justify-between border-b border-border px-4 py-2 text-sm text-muted-foreground">
          {caption}
        </div>
      )}

      <div className={tablePatterns.scroll}>
        <table className={tablePatterns.table}>
          <thead className={tablePatterns.header}>
            <tr>
              {columns.map((col) => {
                const sortable = !!col.sortValue;
                const active = sort?.key === col.key;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={cn(
                      sortable
                        ? tablePatterns.headerCellSortable
                        : tablePatterns.headerCell,
                      cellPadding,
                      col.width,
                      col.hideOnMobile && 'hidden md:table-cell',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center'
                    )}
                    aria-sort={
                      active
                        ? sort?.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                    onClick={
                      sortable
                        ? () => {
                            setSort((prev) => {
                              if (!prev || prev.key !== col.key) {
                                return { key: col.key, direction: 'asc' };
                              }
                              if (prev.direction === 'asc') {
                                return { key: col.key, direction: 'desc' };
                              }
                              return null;
                            });
                          }
                        : undefined
                    }
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.header}
                      {sortable && active && (
                        <span aria-hidden="true">
                          {sort?.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          {isLoading ? (
            <tbody>
              <tr>
                <td colSpan={columns.length} className="p-0">
                  <UniversalLoadingState variant="rows" count={5} />
                </td>
              </tr>
            </tbody>
          ) : error ? (
            <tbody>
              <tr>
                <td
                  colSpan={columns.length}
                  className="p-8 text-center text-sm text-danger"
                >
                  Failed to load: {error.message}
                </td>
              </tr>
            </tbody>
          ) : sortedData.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={columns.length} className="p-0">
                  <UniversalEmptyState
                    title={empty?.title ?? 'No records'}
                    description={empty?.description}
                    icon={empty?.icon}
                    action={empty?.action}
                  />
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody className={tablePatterns.body}>
              {sortedData.map((row, i) => (
                <tr
                  key={getRowKey ? getRowKey(row, i) : i}
                  className={cn(
                    onRowClick ? tablePatterns.rowClickable : tablePatterns.row
                  )}
                  onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row, i);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        cellPadding,
                        col.hideOnMobile && 'hidden md:table-cell',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.align === 'center' && 'text-center'
                      )}
                    >
                      {col.render(row, i)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}
